/**
 * Permisos de la web POR EMPRESA: cada empresa tiene sus roles (los 5 del
 * sistema + roles propios), su catálogo (módulos de plataforma + exclusivos
 * de la empresa), sus módulos apagados y ajustes por persona.
 * Source of truth: SMARTCLOCK_API docs/access/CONTRACT.md (§4 lectura, §5
 * escritura del backoffice, §8 roles propios y catálogo por empresa, §9
 * catálogo ordenado como el menú de la web y sin piso legal) y
 * src/config/access/* (gramática, claves con que nace cada rol, eje staff). Los tipos de este
 * archivo son espejo del contrato; no redefinen reglas: el API es la única
 * autoridad.
 */

// ── Gramática (espejo de src/config/access/grammar.js) ──────────────────

/** Verbos comunes, en el orden fijo de las columnas de la matriz. */
export const COMMON_VERBS = ['view', 'create', 'update', 'delete', 'export', 'approve'] as const;
export type CommonVerb = (typeof COMMON_VERBS)[number];

/** Verbos reservados al eje staff: ningún recurso de producto puede declararlos. */
export const STAFF_VERBS = ['read', 'manage'] as const;

export const VERB_LABELS: Record<CommonVerb, string> = {
  view: 'Ver',
  create: 'Crear',
  update: 'Editar',
  delete: 'Eliminar',
  export: 'Exportar',
  approve: 'Aprobar',
};

/** Segmento válido de un id de recurso o de verbo. */
export const SEGMENT_PATTERN = /^[a-z][a-zA-Z0-9]*$/;
export const RESOURCE_ID_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)*$/;
export const KEY_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/;

// ── Roles de producto (espejo de src/config/access/index.js) ────────────

export type ProductRole = 'worker' | 'supervisor' | 'hr' | 'companyAdmin' | 'external';

export const PRODUCT_ROLES: readonly ProductRole[] = [
  'worker',
  'supervisor',
  'hr',
  'companyAdmin',
  'external',
] as const;

export const PRODUCT_ROLE_LABEL: Record<ProductRole, string> = {
  worker: 'Trabajador',
  supervisor: 'Supervisor',
  hr: 'Recursos Humanos',
  companyAdmin: 'Administrador de empresa',
  external: 'Empresa de servicios transitorios (EST)',
};

/** Roles válidos para una relación laboral (ficha): EST no aplica (va por cuenta). */
export const EMPLOYEE_ROLES: readonly ProductRole[] = [
  'worker',
  'supervisor',
  'hr',
  'companyAdmin',
] as const;

export function isProductRole(role: unknown): role is ProductRole {
  return typeof role === 'string' && (PRODUCT_ROLES as readonly string[]).includes(role);
}

// ── Roles de una empresa (contrato §8.1) ────────────────────────────────

/**
 * Rol de una empresa. Rol del sistema: `key` = rol base, `system: true`
 * (se renombra, no se borra). Rol propio: `key` = slug único en la empresa,
 * `system: false`, con su rol base (`role`) fijo desde que se crea.
 * El rol base decide el alcance de datos (viaja en el token); el rol sólo
 * decide los permisos.
 */
export interface CompanyRole {
  key: string;
  /** Nombre visible; null = el nombre por defecto del rol base. */
  name: string | null;
  role: ProductRole;
  system: boolean;
}

/** Nombre visible de un rol: el propio o, si no tiene, el del rol base. */
export function roleDisplayName(role: Pick<CompanyRole, 'name' | 'role'>): string {
  return role.name?.trim() || PRODUCT_ROLE_LABEL[role.role];
}

// ── Eje staff (espejo de src/config/access/staff-access.js) ─────────────

export type StaffKey =
  | 'access.read'
  | 'access.manage'
  | 'companies.read'
  | 'companies.manage'
  | 'users.read'
  | 'users.manage'
  | 'billing.read'
  | 'billing.manage'
  | 'sales.read'
  | 'sales.manage'
  | 'salesConfig.read'
  | 'salesConfig.manage'
  | 'devices.manage'
  | 'workSchedules.manage'
  | 'catalogs.manage'
  | 'compliance.read'
  | 'incidents.manage'
  | 'maintenance.read'
  | 'maintenance.manage'
  | 'inspection.read'
  | 'emails.read'
  | 'support.read'
  | 'support.manage'
  | 'clockingControl.read'
  | 'clockingControl.manage'
  | 'markingSetup.read'
  | 'markingSetup.manage'
  | 'staff.read'
  | 'staff.manage'
  | 'termination.manage'
  | 'salesTrial.manage';

// ── GET /access/me ─────────────────────────────────────────────────────

/**
 * Textos de una clave de staff (`staffKeys` de /access/me, support-access.spec.md
 * §1.2.1): descripción, área de la matriz «Equipo» y verbo. Vienen todas las del
 * rol, con `granted` en las de la persona, para nombrar también la que falta.
 */
export interface StaffKeyInfo {
  key: string;
  label: string;
  area: string;
  areaLabel: string | null;
  verb: 'read' | 'manage';
  verbLabel: string;
  granted: boolean;
}

export interface AccessMe {
  principal: 'product' | 'staff' | 'inspector';
  permissions: string[];
  staff: string[];
  /** Solo staff: los textos de las claves (el backoffice no guarda copias). */
  staffKeys?: StaffKeyInfo[];
  company: { id: string; name: string } | null;
  role: string | null;
  /** Rol que aplica en la empresa del token (§8.4); null para staff/inspector. */
  accessRole?: { key: string; name: string | null; system: boolean; baseRole: string } | null;
  disabledModules: string[];
  catalogVersion: number;
  accessVersion: number;
}

// ── Catálogo (GET /admin/access/catalog = GET /access/catalog) ──────────

export interface CatalogVerb {
  id: string;
  label: string;
  key: string;
  /** false = ninguna ruta del API exige esta clave todavía. */
  enforced: boolean;
}

export interface CatalogSpecial extends CatalogVerb {
  icon: string | null;
  description: string | null;
}

export interface CatalogResource {
  resourceId: string;
  label: string;
  icon: string | null;
  verbs: CatalogVerb[];
  specials: CatalogSpecial[];
  /** Clave de la que depende el recurso (p. ej. `alerts.view`). */
  requires: string | null;
  sensitive: boolean;
  order: number;
  source: 'seed' | 'custom';
  /** null = módulo de plataforma; con id = módulo exclusivo de esa empresa (§8.3). */
  companyId: string | null;
  /** Nombre de la empresa dueña (sólo en el catálogo completo del backoffice). */
  companyName: string | null;
}

/**
 * Módulo del catálogo (`modules` de GET /access/catalog): agrupa SECCIONES, no
 * recursos. Hoy el API entrega el «Módulo DT» (lo que exige la Res. Ex.
 * N°38/2024) y lo que queda fuera de él. Es sólo agrupación: estar en un
 * módulo no concede nada; cada pantalla sigue con su permiso propio.
 */
export interface CatalogModule {
  id: string;
  label: string;
  description: string | null;
  /** Orden entre módulos. */
  order: number;
  source: 'seed' | 'custom';
}

/** Módulo de una sección que no declara el suyo (API anterior a `modules`). */
export const FALLBACK_MODULE_ID = 'general';

export interface CatalogSection {
  id: string;
  label: string;
  /** Id del módulo (`AccessCatalog.modules`); sin él, `FALLBACK_MODULE_ID`. */
  module: string;
  /** Ícono del menú de la web (Material Symbols); el backoffice sólo lo muestra. */
  icon: string | null;
  /** Orden dentro de su módulo; null en una sección extra (no está en la estructura). */
  order: number | null;
  source: 'seed' | 'custom';
  resources: CatalogResource[];
}

// ── Estructura del catálogo: borrador y publicar (access-catalog-layout.spec.md) ──

/** Un módulo del catálogo (pantalla) dentro de una sección de la estructura. */
export interface LayoutResourceItem {
  resourceId: string;
  label: string;
  source: 'seed' | 'custom';
  companyId: string | null;
  companyName: string | null;
  /** Sólo en `unplaced`: la sección que nombra y que no existe. */
  section?: string;
}

export interface LayoutModule {
  id: string;
  label: string;
  description: string | null;
}

export interface LayoutSection {
  /** null = sección nueva del borrador (el API le da un id al guardarlo). */
  id: string | null;
  label: string;
  /** Id del módulo (`LayoutModule.id`). */
  module: string;
  /** Ícono del menú de la web («Material Icons»). */
  icon: string | null;
  source: 'seed' | 'custom';
  resources: LayoutResourceItem[];
}

/** Módulos y secciones en su orden, con sus pantallas; `unplaced`: las que no tienen sección. */
export interface LayoutView {
  modules: LayoutModule[];
  sections: LayoutSection[];
  unplaced: LayoutResourceItem[];
}

export interface LayoutDraftInfo {
  updatedAt: string | null;
  updatedBy: { id: string; name: string | null; email: string } | null;
  /** `catalogVersion` cuando se empezó el borrador. */
  baseCatalogVersion: number;
  /** El catálogo cambió después de empezar el borrador. */
  stale: boolean;
}

/** Un módulo editado en el borrador, como quedará al publicar (misma forma que el catálogo). */
export interface DraftResource extends Omit<CatalogResource, 'companyName'> {
  updatedAt: string | null;
}

/**
 * GET /admin/access/catalog/layout: lo publicado y, si hay, el borrador. `draft` es la estructura del
 * borrador (null si no la cambia) y `resources`, los módulos editados. `draftInfo` está mientras haya
 * algo sin publicar.
 */
export interface CatalogLayoutState {
  catalogVersion: number;
  published: LayoutView;
  draft: LayoutView | null;
  resources: DraftResource[];
  draftInfo: LayoutDraftInfo | null;
}

/** PUT /admin/access/catalog/resources/:resourceId/draft: cómo queda el módulo (sin sección ni orden). */
export type ResourceDraftPayload = Omit<
  CatalogResourcePayload,
  'resourceId' | 'section' | 'order' | 'companyId'
>;

/** PUT /admin/access/catalog/layout/draft: el borrador completo, en orden. */
export interface LayoutDraftPayload {
  modules: { id: string; label: string; description: string | null }[];
  sections: {
    id: string | null;
    label: string;
    module: string;
    icon: string | null;
    resourceIds: string[];
  }[];
}

/** GET /reference/menu-icons: los íconos que se pueden elegir para el menú. */
export interface MenuIconCatalog {
  font: string;
  icons: string[];
}

export interface AccessCatalog {
  surface: string;
  version: number;
  hash: string | null;
  source: 'db' | 'seed';
  commonVerbs: { id: string; label: string }[];
  /** Módulos en su orden; vacío si el API todavía no los entrega (sin agrupación). */
  modules: CatalogModule[];
  /** Secciones en el orden del menú lateral de la web (§9); sus módulos, por `order`. */
  sections: CatalogSection[];
  /**
   * Claves con que NACE la plantilla de cada rol (§9.3). Sólo informativo:
   * se editan como cualquier otra y nada las impone.
   */
  roleDefaults: Partial<Record<ProductRole, string[]>>;
}

/** Cuerpo de POST/PUT de un recurso del catálogo (sin `source`: queda `custom`). */
export interface CatalogResourcePayload {
  resourceId?: string;
  section: string;
  label: string;
  icon: string | null;
  verbs: string[];
  specials: { id: string; label: string; icon: string | null; description: string | null }[];
  requires: string | null;
  sensitive: boolean;
  /** Posición; al crear, si falta, va al final. El orden en la sección se cambia en «Organizar». */
  order?: number;
  /** Sólo al crear: id de la empresa para un módulo exclusivo; sin él, de plataforma. */
  companyId?: string | null;
}

export interface DeleteResourceResult {
  deleted: boolean;
  /** Formato por confirmar con el API: lista de referencias o un conteo. */
  referencedBy: unknown;
}

// ── Módulos por empresa ────────────────────────────────────────────────

export interface CompanyModules {
  disabledModules: string[];
  /** Módulos exclusivos de la empresa (§8.4); se apagan igual que los de plataforma. */
  exclusive: CatalogResource[];
}

// ── Plantillas ─────────────────────────────────────────────────────────

export type TemplateState = 'draft' | 'published';

/** Referencia a un usuario tal como puede venir del API (id o populado). */
export type AccessActor =
  | string
  | {
      id?: string | null;
      _id?: string;
      name?: string | null;
      email?: string | null;
      firstName?: string;
      lastName?: string;
      role?: string | null;
    }
  | null
  | undefined;

export interface AccessTemplate {
  id: string;
  surface: string;
  /** null = plantilla base de plataforma (sólo roles del sistema). */
  company: string | null;
  /** Clave del rol en la empresa: igual al rol base en los del sistema (§8.1). */
  key: string;
  /** Rol base (alcance de datos). */
  role: ProductRole;
  /** true = uno de los 5 roles del sistema (no se borra). */
  system: boolean;
  name: string | null;
  description: string | null;
  icon: string | null;
  /** Copia de trabajo (borrador). */
  permissions: string[];
  state: TemplateState;
  version: number;
  publishedAt: string | null;
  publishedBy: AccessActor;
  /** Lo que APLICA hoy: la foto de la última publicación. */
  publishedPermissions: string[];
  active: boolean;
  updatedAt?: string;
  createdAt?: string;
  /** Sólo en GET /admin/access-templates/:id. */
  affectedUsers?: number;
}

/**
 * Fila de GET /admin/companies/:companyId/access-templates (§8.4): una por
 * cada rol de la empresa, primero los 5 del sistema (con `template: null` si
 * falta) y luego los propios. En GET /admin/access-templates/defaults sólo
 * vienen los del sistema.
 */
export interface CompanyTemplateRow extends CompanyRole {
  template: AccessTemplate | null;
  /** Rol propio: personas con ese rol asignado; sistema: contextos sin rol propio. */
  affectedUsers: number;
}

/** POST de un rol del sistema (sólo `role`) o de una plantilla base. */
export interface CreateTemplatePayload {
  role: ProductRole;
  name?: string;
  description?: string;
  permissions?: string[];
  cloneFrom?: 'default';
}

/**
 * POST de un rol propio (§8.4). `cloneFrom`: key de otro rol de la empresa o
 * `'default'` (plantilla base del rol base); sin él arranca con las claves con que
 * nace ese rol base (`roleDefaults`: vacío salvo el trabajador).
 */
export interface CreateCompanyRolePayload {
  name: string;
  role: ProductRole;
  description?: string;
  cloneFrom?: string;
}

export interface UpdateTemplatePayload {
  name?: string | null;
  description?: string | null;
  icon?: string | null;
  permissions?: string[];
}

export interface PermissionDiff {
  gained: string[];
  lost: string[];
}

export interface PublishResult {
  diff: PermissionDiff;
  affectedUsers: number;
  version: number;
  published: boolean;
  dryRun?: boolean;
}

export interface TemplateHistoryEntry {
  version: number | null;
  action: string;
  actor: AccessActor;
  at: string;
  gained: string[];
  lost: string[];
}

/**
 * Fila del historial con el contrato de grilla (`GET …/history` con `q`): la API agrega la etiqueta
 * de la acción, el nombre del autor y los conteos, para ordenar por lo que se ve.
 */
export interface TemplateHistoryRow extends TemplateHistoryEntry {
  id: string;
  actionLabel: string;
  actorName: string;
  gainedCount: number;
  lostCount: number;
}

/** Desglose por empresa del dry-run de «Aplicar a otras empresas». */
export interface ApplyBreakdownRow {
  companyId: string;
  /** Nombre de la empresa (el API lo manda como `name`). */
  name?: string | null;
  /** Formato por confirmar: lista de claves o conteo. */
  gained: string[] | number;
  lost: string[] | number;
  affectedUsers: number;
}

export interface ApplyDryRunResult {
  /** sha256 hex (64). Se devuelve tal cual al confirmar. */
  fingerprint: string;
  breakdown: ApplyBreakdownRow[];
  role?: string;
  version?: number;
}

export type ApplyStatus = 'applied' | 'unchanged' | 'conflict';

export interface ApplyOutcome extends ApplyBreakdownRow {
  status: ApplyStatus;
  templateId: string | null;
  version?: number;
}

export interface ApplyResult {
  applied: ApplyOutcome[];
  role?: string;
  version?: number;
}

// ── Ajustes por persona ────────────────────────────────────────────────

export interface UserAccess {
  /** Rol base con que opera en la empresa. */
  role: string | null;
  /**
   * Todos los roles base con que la persona entra a la empresa (el ajuste
   * aplica a todos). En el API v2 viene como `baseRoles`; `roles` del API son
   * los roles de la empresa y se normalizan en `availableRoles`.
   */
  roles: string[];
  /** Rol asignado en la empresa tal como está guardado (§8.2); null = el del sistema. */
  roleKey: string | null;
  /**
   * Rol que APLICA según el API: el asignado sólo si existe, está activo,
   * tiene el rol base de la persona y se publicó alguna vez; si no, el del sistema.
   */
  accessRole: { key: string; name: string | null; system: boolean; baseRole: string } | null;
  /** Roles disponibles de la empresa (§8.4: `roles` con objetos `{ key, name, role, system }`). */
  availableRoles: CompanyRole[];
  /** Plantilla que aplica (`permissions` = lo publicado); null si no hay. */
  template: { id: string; version: number; state?: TemplateState; permissions: string[] } | null;
  granted: string[];
  revoked: string[];
  disabledModules: string[];
  effective: string[];
}

export interface UserAccessPayload {
  companyId: string;
  granted: string[];
  revoked: string[];
  /**
   * Rol a asignar (§8.2). Si su rol base difiere del de la persona, el API
   * cambia también el rol base y cierra sus sesiones.
   */
  roleKey?: string | null;
}

export interface UserAccessHistoryEntry {
  action: string;
  actor: AccessActor;
  at: string;
  /** Formato por confirmar: se espera `{ granted, revoked }`. */
  before: unknown;
  after: unknown;
}

// ── Matriz ─────────────────────────────────────────────────────────────

/**
 * Estados de celda de la matriz:
 * - on: concedido (y publicado / guardado).
 * - off: no concedido.
 * - adding / removing: diferencia del borrador contra lo publicado (o lo guardado).
 * - revoked: retirado a la persona (gana sobre la plantilla).
 * - inherited: viene de la plantilla del rol (ajustes por persona).
 * - blocked: falta la dependencia `requires` del recurso.
 * - na: el recurso no declara ese verbo.
 */
export type CellState =
  'on' | 'off' | 'adding' | 'removing' | 'revoked' | 'inherited' | 'blocked' | 'na';

export type MatrixMode = 'template' | 'person';

/** Ajustes por persona emitidos por la matriz en modo `person`. */
export interface PersonOverrides {
  granted: string[];
  revoked: string[];
}
